
import { Vue, Component, Watch } from 'vue-property-decorator'
import Delivery from '@/types/delivery'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import PageHeader from '@/components/base/PageHeader.vue'
import { DeliveryStateLabelColors, DeliveryStateLabels } from '@/globalValues'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, PageHeader, ActiveFilters }
})

export default class Deliveries extends Vue {
  settings = {
    routeNames: { create: 'deliveryCreate', show: 'deliveryShow', edit: 'deliveryEdit' },
    routeParamKey: 'deliveryId',
    permissionNames: { show: 'deliveries.view', edit: 'deliveries.edit', delete: 'deliveries.delete' },
    functionNames: { getList: 'getDeliveryList' },
    getterNames: { list: 'deliveryList', loading: 'loadingDelivery' },
    localStorageName: 'deliveryTableSettings'
  }

  columns:Column[] = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      scopedSlots: { customRender: 'project', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Bouwnummer',
      key: 'building.number',
      dataIndex: 'building',
      scopedSlots: { customRender: 'building' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'VS ingepland voor',
      key: 'inspection_scheduled_user.id',
      dataIndex: 'inspection_scheduled_user',
      scopedSlots: { customRender: 'inspection_scheduled_user', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'VS ingepland op',
      key: 'inspection_scheduled_at',
      dataIndex: 'inspection_scheduled_at',
      scopedSlots: { customRender: 'inspection_scheduled_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'VS voltooid door',
      key: 'inspection_completed_user.id',
      dataIndex: 'inspection_completed_user',
      scopedSlots: { customRender: 'inspection_completed_user', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'VS voltooid op',
      key: 'inspection_completed_at',
      dataIndex: 'inspection_completed_at',
      scopedSlots: { customRender: 'inspection_completed_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'VS status',
      key: 'inspection_status',
      dataIndex: 'inspection_status',
      scopedSlots: { customRender: 'inspection_status' },
      filters: [
        {
          text: 'Ingepland',
          value: 1
        },
        {
          text: 'Concept',
          value: 2
        },
        {
          text: 'Verlopen',
          value: 3
        },
        {
          text: 'Voltooid',
          value: 4
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'OPL ingepland voor',
      key: 'delivery_scheduled_user.id',
      dataIndex: 'delivery_scheduled_user',
      scopedSlots: { customRender: 'delivery_scheduled_user', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'OPL ingepland op',
      key: 'delivery_scheduled_at',
      dataIndex: 'delivery_scheduled_at',
      scopedSlots: { customRender: 'delivery_scheduled_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'OPL voltooid door',
      key: 'delivery_completed_user.id',
      dataIndex: 'delivery_completed_user',
      scopedSlots: { customRender: 'delivery_completed_user', filterDropdown: 'customFilter' },
      onFilterDropdownVisibleChange: (visible:boolean):void => this.filterDropdownVisibleChange(visible),
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'OPL voltooid op',
      key: 'delivery_completed_at',
      dataIndex: 'delivery_completed_at',
      scopedSlots: { customRender: 'delivery_completed_at' },
      filters: [],
      sorter: true,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'OPL status',
      key: 'delivery_status',
      dataIndex: 'delivery_status',
      scopedSlots: { customRender: 'delivery_status' },
      filters: [
        {
          text: 'Ingepland',
          value: 1
        },
        {
          text: 'Concept',
          value: 2
        },
        {
          text: 'Verlopen',
          value: 3
        },
        {
          text: 'Voltooid',
          value: 4
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: null,
    search: null
  }

  selectedRowKeys:number[] = []
  filtersLoaded = false

  mounted () : void {
    this.reload()
    this.getFilters()
    this.onTableSettingsChange()
  }

  reloadList () : void {
    this.selectedRowKeys = []
    this.reload()
  }

  reload () :void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  async getFilters () : Promise<void> {
    await this.getProjectFilter()
    await this.getInspectionScheduledUserFilter()
    await this.getInspectionCompletedUserFilter()
    await this.getDeliveryScheduledUserFilter()
    await this.getDeliveryCompletedUserFilter()
    this.filtersLoaded = true
  }

  getProjectFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/project/deliveries', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[1].filters = res.data
    })
  }

  getInspectionScheduledUserFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/user/scheduled_inspections', { textColumn: 'full_name', valueColumn: 'id' }).then((res) => {
      this.columns[3].filters = res.data
    })
  }

  getInspectionCompletedUserFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/user/completed_inspections', { textColumn: 'full_name', valueColumn: 'id' }).then((res) => {
      this.columns[5].filters = res.data
    })
  }

  getDeliveryScheduledUserFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/user/scheduled_deliveries', { textColumn: 'full_name', valueColumn: 'id' }).then((res) => {
      this.columns[8].filters = res.data
    })
  }

  getDeliveryCompletedUserFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/user/completed_deliveries', { textColumn: 'full_name', valueColumn: 'id' }).then((res) => {
      this.columns[10].filters = res.data
    })
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.reload()
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.reload()
  }

  removedFilter (activeFilters:{[key: string]: number[]|string[]}) : void {
    // Set updated filters.
    this.tableSettings.activeFilters = activeFilters

    // Get list based on filters and search term.
    this.reload()
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.reload()
  }

  filterVisible = false
  filterDropdownVisibleChange (visible:boolean) : void {
    this.filterVisible = visible
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          if (can(this.settings.permissionNames.show)) {
            const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          }
        }
      }
    }
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  get itemList () : { data: Delivery[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Voorschouw/Oplevering ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get DeliveryStateLabelColors () : { [key: number]: string } {
    return DeliveryStateLabelColors
  }

  get DeliveryStateLabels () : { [key: number]: string } {
    return DeliveryStateLabels
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
